import { useState } from "react";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import styles from "./style.module.scss";
import { CARDINAL_DIRECTIONS, calculateDirectionSelection, calculateRangeForGroup, createEmptyConditionLevel, groupAdjacentDirections, serializeSpotInfo } from "../../libs/spot/spotData";

const levels = ["ideal", "good", "regular", "poor"];
const levelLabels = { ideal: "Ideal", good: "Bom", regular: "Regular", poor: "Ruim" };

export function SpotInformation({ value, onChange }) {
  const [openLevel, setOpenLevel] = useState("ideal");
  const serializedConditions = serializeSpotInfo(value).conditions;
  const setField = (path, nextValue) => {
    const [level, group, field] = path;
    onChange({
      ...value,
      conditions: {
        ...value.conditions,
        [level]: { ...value.conditions[level], [group]: { ...value.conditions[level][group], [field]: nextValue } },
      },
    });
  };
  const setDirections = (level, group, selectedDirections) => {
    const current = value.conditions[level][group];
    onChange({
      ...value,
      conditions: {
        ...value.conditions,
        [level]: {
          ...value.conditions[level],
          [group]: {
            ...current,
            ...calculateDirectionSelection(selectedDirections),
            selected_directions: selectedDirections,
            directions_touched: true,
          },
        },
      },
    });
  };
  const setLevelEnabled = (level, enabled) => {
    onChange({
      ...value,
      conditions: {
        ...value.conditions,
        [level]: enabled ? createEmptyConditionLevel() : null,
      },
    });
  };
  const textField = (field, label, multiline = false, type = "text") => (
    <label className={styles.field}>
      <span>{label}</span>
      {multiline ? <textarea value={value[field]} onChange={(event) => onChange({ ...value, [field]: event.target.value })} /> : <input type={type} value={value[field]} onChange={(event) => onChange({ ...value, [field]: event.target.value })} />}
    </label>
  );
  const numberField = (level, group, field, label) => (
    <label className={styles.field}><span>{label}</span><input type="number" step="any" value={value.conditions[level][group][field]} onChange={(event) => setField([level, group, field], event.target.value)} /></label>
  );
  const original = (label, generated) => <label className={styles.field}><span>{label}</span><input value={generated || "Gerado automaticamente ao salvar"} disabled /></label>;

  return (
    <section className={styles.spotSection}>
      <div className={styles.heading}><h3>Informações do spot de surf</h3><p>Contexto, localização e leitura de mar organizados em um só lugar.</p></div>
      <div className={styles.grid}>
        {textField("season", "Temporada")}
        {textField("aliases", "Aliases", false)}
      </div>
      <p className={styles.hint}>Separe aliases por vírgulas. Espaços extras e itens vazios não serão salvos.</p>
      <div className={styles.stack}>
        {textField("spot_description", "Descrição do spot", true)}
        {textField("observations", "Observações", true)}
        {textField("notes", "Notas", true)}
      </div>
      <div className={styles.conditions}><h4>Condições</h4>{levels.map((level) => {
        const conditionLevel = value.conditions[level];
        const hasConditions = conditionLevel !== null;
        return <div className={styles.level} key={level}>
          <button type="button" className={styles.levelButton} onClick={() => setOpenLevel(openLevel === level ? "" : level)}><span>{levelLabels[level]}</span><small>{openLevel === level ? "Ocultar" : "Editar"}</small></button>
          {openLevel === level && <div className={styles.levelContent}>
            <label className={styles.levelToggle}><input type="checkbox" checked={hasConditions} onChange={(event) => setLevelEnabled(level, event.target.checked)} /><span>Este nível possui condições</span></label>
            {!hasConditions && <p className={styles.noConditions}>Sem condições cadastradas.</p>}
            {hasConditions && <>
              <div className={styles.group}><h5>Tamanho</h5><div className={styles.grid}>{numberField(level, "size", "min_m", "Mínimo (m)")}{numberField(level, "size", "max_m", "Máximo (m)")}{original("Original", serializedConditions[level].size.original)}</div></div>
              <div className={styles.group}><h5>Direção do swell</h5><DirectionPicker condition={conditionLevel.swell_direction} onChange={(directions) => setDirections(level, "swell_direction", directions)} /><div className={styles.grid}>{original("Original", serializedConditions[level].swell_direction.original)}</div></div>
              <div className={styles.group}><h5>Período</h5><div className={styles.grid}>{numberField(level, "period", "min_s", "Mínimo (s)")}{numberField(level, "period", "max_s", "Máximo (s)")}{original("Original", serializedConditions[level].period.original)}</div></div>
              <div className={styles.group}><h5>Vento</h5><DirectionPicker condition={conditionLevel.wind} onChange={(directions) => setDirections(level, "wind", directions)} /><div className={styles.grid}>{original("Original", serializedConditions[level].wind.original)}</div><div className={styles.flags}><label><input type="checkbox" checked={conditionLevel.wind.calm_tolerant} onChange={(event) => setField([level, "wind", "calm_tolerant"], event.target.checked)} /> Tolera mar calmo</label><label className={styles.onshoreControl}><span>Onshore<select value={conditionLevel.wind.onshore} onChange={(event) => setField([level, "wind", "onshore"], event.target.value)}><option value="">Sem informação</option><option value="true">Sim</option><option value="false">Não</option></select></span><small>Vento que vem do mar em direção à costa. “Sem informação” não grava essa regra.</small></label></div></div>
            </>}
          </div>}
        </div>;
      })}</div>
    </section>
  );
}

function DirectionPicker({ condition, onChange }) {
  const selected = condition.selected_directions || [];
  const ranges = groupAdjacentDirections(selected).map(calculateRangeForGroup);
  const primary = ranges.find((range) => range.directions.includes(selected[0]));
  const alternatives = ranges.filter((range) => range !== primary);
  const describeRange = (range) => `${range.directions.map((code) => CARDINAL_DIRECTIONS.find((direction) => direction.code === code).name).join(" + ")} (${range.min_deg}°–${range.max_deg}°)`;

  return <div className={styles.directionPicker}>
    <div className={styles.directionChips}>
      {CARDINAL_DIRECTIONS.map((direction) => <button key={direction.code} type="button" className={selected.includes(direction.code) ? styles.directionChipActive : styles.directionChip} onClick={() => onChange(selected.includes(direction.code) ? selected.filter((code) => code !== direction.code) : [...selected, direction.code])}>{direction.displayCode}</button>)}
    </div>
    <p className={styles.directionExplanation}>As faixas definem os quadrantes de swell ou vento aceitos para este nível. Direções contínuas viram uma faixa; direções separadas viram alternativas.</p>
    {primary && <div className={styles.directionSummary}><span>Faixa principal: {describeRange(primary)}</span>{alternatives.length > 0 && <span>Faixas alternativas: {alternatives.map(describeRange).join(" • ")}</span>}</div>}
    {!primary && <div className={styles.directionWarning}><WarningAmberRoundedIcon /><span>Faixa de direção não definida.</span></div>}
  </div>;
}
